import clsx from 'clsx';
import React, { useEffect } from 'react';

import Button from '@components/Button';
import { PrimaryButton, ProButton } from '@components/Button/variants';
import IconDeepResearchDoubleSparkle from '@components/Images/DeepResearch/IconDeepResearchDoubleSparkle';
import useEventTracking from '@hooks/useEventTracking';
import useReturningUrl from '@hooks/useReturningUrl';
import {
  deep_research_path,
  events_path,
  finance_path,
  graphics_path,
  new_session_path,
  newsletter_path,
  subscribe_path,
  technology_path,
  titv_path,
  upgrade_subscription_path,
  weekend_path,
} from '@routes';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { NavCurrentUser } from '@utils/backend-api/currentUser';

import DefaultSubmenuNavItem from '../SubmenuNav/DefaultSubmenuNav/NavItem';
import NavItemList from '../TopNav/NavItemList';
import {
  ABOUT_PRO_HREF,
  COMMUNITY_SUBMENU_NAV_ITEMS,
  MOBILE_SIDE_NAV_ITEMS,
  MORE_SUBMENU_MAIN_NAV_ITEMS,
  MORE_SUBMENU_SOCIAL_NAV_ITEMS,
  MORE_SUBNAV_FOOTER_NAV_ITEMS,
  ProNavSections,
} from '../navItems';
import ExpandableMenu from './ExpandableMenu';
import MenuLink, { MenuLinkTypes } from './MenuLink';

interface SideBarProps {
  currentUser: NavCurrentUser;
  isSideNavOpen: boolean;
  setIsSideNavOpen: (isSideNavOpen: boolean) => void;
  proNavSections: ProNavSections;
}

const SideNav = ({ currentUser, isSideNavOpen, proNavSections }: SideBarProps) => {
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.SideNav,
  });
  const dataToolsProMenuItems = proNavSections?.data_tools ?? [];
  const specialProMenuItems = proNavSections?.special ?? [];
  const proNavItems = currentUser.isProSubscriber
    ? dataToolsProMenuItems
    : [
        {
          name: 'About Pro',
          href: ABOUT_PRO_HREF,
        },
        ...dataToolsProMenuItems,
      ];
  const returningUrl = useReturningUrl();
  const mobileSideNavItems = !currentUser.isLoggedIn
    ? [
        {
          name: 'Sign in',
          href: new_session_path({ returning_url: returningUrl }),
        },
        ...MOBILE_SIDE_NAV_ITEMS,
      ]
    : MOBILE_SIDE_NAV_ITEMS;

  useEffect(() => {
    const topBanners = document.querySelectorAll('.top-sticky-banner');

    if (isSideNavOpen) {
      document.body.style.overflow = 'hidden';
      if (topBanners) {
        // Hide the top banner when the side nav is open to prevent the design from breaking
        topBanners.forEach((banner: HTMLElement) => (banner.style.display = 'none'));
      }
    } else {
      document.body.style.overflow = 'unset';
      if (topBanners) {
        topBanners.forEach((banner: HTMLElement) => (banner.style.display = 'block'));
      }
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSideNavOpen]);

  return (
    <div
      className={clsx(
        'fixed left-0 right-0 top-56 z-[10000] h-[calc(100vh-56px)] overflow-y-scroll bg-grayscale-charcoal duration-300 ease-in nav:hidden dark:bg-[var(--light-grayscale-charcoal)]',
        {
          '-translate-x-full': !isSideNavOpen,
          'translate-x-0': isSideNavOpen,
        }
      )}
      tabIndex={-1}
    >
      <nav className="flex flex-col gap-12 border-0 border-b border-solid border-grayscale-beige px-36 pb-[30px] pt-24">
        <ExpandableMenu
          name="Pro"
          onClickCapture={() =>
            trackEvent(click({ clickTarget: ClickTarget.DropDown, clickText: 'Pro' }))
          }
          isPro
          showSeparator
        >
          <NavItemList className="space-y-10">
            {proNavItems.map(({ name, ...rest }) => (
              <DefaultSubmenuNavItem
                as="link"
                key={name}
                onClickCapture={() =>
                  trackEvent(click({ clickTarget: ClickTarget.ProMenuItem, clickText: name }))
                }
                {...rest}
              >
                {name}
              </DefaultSubmenuNavItem>
            ))}
          </NavItemList>
          <h2 className="mt-32 font-suisse-intl-mono text-overline-md font-bold text-pro-gold">
            SPECIAL PROJECTS
          </h2>
          <NavItemList className="space-y-10">
            {specialProMenuItems.map(({ name, ...rest }) => (
              <DefaultSubmenuNavItem
                as="link"
                key={name}
                onClickCapture={() =>
                  trackEvent(click({ clickTarget: ClickTarget.ProMenuItem, clickText: name }))
                }
                {...rest}
              >
                {name}
              </DefaultSubmenuNavItem>
            ))}
          </NavItemList>
        </ExpandableMenu>
        <MenuLink
          name="Deep Research"
          as={MenuLinkTypes.Link}
          href={deep_research_path()}
          icon={<IconDeepResearchDoubleSparkle className="w-[17px] text-pro-gold" />}
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.DeepResearch }))}
          showSeparator
        />
        <MenuLink
          name="TITV"
          as={MenuLinkTypes.Link}
          href={titv_path()}
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.TITV }))}
          showSeparator
        />
        <MenuLink
          name="Tech"
          as={MenuLinkTypes.Link}
          href={technology_path()}
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Tech }))}
          showSeparator
        />
        <MenuLink
          name="Finance"
          as={MenuLinkTypes.Link}
          href={finance_path()}
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Finance }))}
          showSeparator
        />
        <MenuLink
          name="Weekend"
          as={MenuLinkTypes.Link}
          href={weekend_path()}
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Weekend }))}
          showSeparator
        />
        <MenuLink
          name="Charts"
          as={MenuLinkTypes.Link}
          href={graphics_path()}
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Charts }))}
          showSeparator
        />
        <MenuLink
          name="Events"
          as={MenuLinkTypes.Link}
          href={events_path()}
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.EventsMenuItem }))}
          showSeparator
        />
        <MenuLink
          name="Newsletters"
          as={MenuLinkTypes.Link}
          href={newsletter_path()}
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.ViewAllNewsletters }))}
          showSeparator
        />
        <ExpandableMenu
          name="Community"
          onClickCapture={() =>
            trackEvent(click({ clickTarget: ClickTarget.DropDown, clickText: 'Community' }))
          }
          showSeparator
        >
          <NavItemList className="space-y-24">
            {COMMUNITY_SUBMENU_NAV_ITEMS.map(({ name, ...rest }) => (
              <DefaultSubmenuNavItem
                as="link"
                key={name}
                onClickCapture={() =>
                  trackEvent(click({ clickTarget: ClickTarget.CommunityMenuItem, clickText: name }))
                }
                {...rest}
              >
                {name}
              </DefaultSubmenuNavItem>
            ))}
          </NavItemList>
        </ExpandableMenu>
        <ExpandableMenu
          name="More"
          onClickCapture={() =>
            trackEvent(click({ clickTarget: ClickTarget.DropDown, clickText: 'More' }))
          }
        >
          <NavItemList className="space-y-24">
            {MORE_SUBMENU_MAIN_NAV_ITEMS.map(({ name, ...rest }) => (
              <DefaultSubmenuNavItem
                as="link"
                key={name}
                onClickCapture={() =>
                  trackEvent(click({ clickTarget: ClickTarget.MoreMenuItem, clickText: name }))
                }
                {...rest}
              >
                {name}
              </DefaultSubmenuNavItem>
            ))}
          </NavItemList>
        </ExpandableMenu>
      </nav>
      <nav className="h-screen min-h-webkit-fill-available px-36 pb-48 pt-[30px]">
        {!currentUser.isSubscriber && (
          <PrimaryButton
            as="link"
            href={subscribe_path()}
            className="mb-32 w-full"
            onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Subscribe }))}
          >
            Subscribe
          </PrimaryButton>
        )}
        {currentUser.isSubscriber && !currentUser.isProSubscriber && (
          <ProButton
            as="link"
            className="mb-32 w-full"
            href={upgrade_subscription_path()}
            onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Upgrade }))}
          >
            Upgrade to Pro
          </ProButton>
        )}
        <NavItemList className="space-y-32">
          {mobileSideNavItems.map(({ name, ...rest }) => (
            <DefaultSubmenuNavItem
              as="link"
              key={name}
              className="font-suisse-screen text-overline-lg"
              onClickCapture={() =>
                trackEvent(click({ clickTarget: ClickTarget.SideNavItem, clickText: name }))
              }
              {...rest}
            >
              {name}
            </DefaultSubmenuNavItem>
          ))}
        </NavItemList>
        <NavItemList className="mt-40 flex gap-[18px]">
          {MORE_SUBMENU_SOCIAL_NAV_ITEMS.map(({ icon: Icon, name, ...rest }) => (
            <Button
              key={name}
              as="externalLink"
              className="flex h-[35px] w-[35px] items-center justify-center rounded-full border border-solid border-white bg-transparent text-white transition-colors hover:border-primary-magenta hover:text-primary-magenta focus:border-primary-magenta focus:text-primary-magenta dark:hover:text-primary-pink"
              onClickCapture={() =>
                trackEvent(click({ clickTarget: ClickTarget.SocialNavItem, clickText: name }))
              }
              {...rest}
            >
              <Icon className="h-12" />
              <span className="sr-only">{name}</span>
            </Button>
          ))}
        </NavItemList>
        <NavItemList className="mt-32 flex justify-center gap-24">
          {MORE_SUBNAV_FOOTER_NAV_ITEMS.map(({ name, hasExternalLink, ...rest }) => (
            <li key={name}>
              <Button
                as={hasExternalLink ? 'externalLink' : 'link'}
                className="font-suisse-screen text-disclosure-md leading-[1.3] text-white opacity-50 transition-all hover:text-primary-magenta hover:opacity-100 focus:opacity-100 dark:hover:text-primary-pink"
                onClickCapture={() =>
                  trackEvent(click({ clickTarget: ClickTarget.FinePrintNavItem, clickText: name }))
                }
                {...rest}
              >
                {name}
              </Button>
            </li>
          ))}
        </NavItemList>
      </nav>
    </div>
  );
};

export default SideNav;
